import { createImageVariantConfig } from '../../util/sdkLoader';
import { isErrorUserPendingApproval, isForbiddenError, storableError } from '../../util/errors';
import { convertUnitToSubUnit, unitDivisor } from '../../util/currency';
import {
  parseDateFromISO8601,
  getExclusiveEndDate,
  addTime,
  subtractTime,
  daysBetween,
  getStartOf,
} from '../../util/dates';
import { constructQueryParamName, isOriginInUse, resolveDefaultSortKey } from '../../util/search';
import { parse, stringify } from '../../util/urlHelpers';
import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { isPublicMoorageListing } from '../../util/listingHelpers';
import { normalizeAccessFilter } from '../../util/publicMoorages';
import {
  ACCESS_FILTER_ALL,
  ACCESS_FILTER_PRIVATE,
  ACCESS_FILTER_PUBLIC,
  ACCESS_FILTER_QUERY_PARAM,
} from '../../util/publicMoorageConstants';

// Pagination page size might need to be dynamic on responsive page layouts
// Current design has max 3 columns 12 is divisible by 2 and 3
// So, there's enough cards to fill all columns on full pagination pages
const RESULT_PAGE_SIZE = 100;
let latestSearchRequestCounter = 0;
const MAP_SEARCH_PUBLIC_MOORAGE_HYDRATE_DELAY_MS = 450;
const inFlightSearchRequests = new Map();
const pendingMapHydrationTimeouts = new Map();

const nextSearchRequestId = () => {
  latestSearchRequestCounter += 1;
  return latestSearchRequestCounter;
};

const debugSearchLog = (event, details) => {
  if (process.env.NODE_ENV === 'production') {
    return;
  }

  // eslint-disable-next-line no-console
  console.debug(`[SearchPage] ${event}`, details);
};

// Helper to parse and extract common query parameters
const extractQueryParams = (search, config) => {
  const queryParams = parse(search, {
    latlng: ['origin'],
    latlngBounds: ['bounds'],
  });

  const { page = 1, origin, ...rest } = queryParams;
  const originMaybe = isOriginInUse(config) && origin ? { origin } : {};

  return { page, originMaybe, rest };
};

// Helper to create common search parameters
const createSearchParams = (config, queryParams) => {
  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config.layout.listingImage;
  const aspectRatio = aspectHeight / aspectWidth;

  const { pub_infoOnly, publicMoorages, ...rest } = queryParams.rest || {};
  const sortParamName = config?.search?.sortConfig?.queryParamName || 'sort';
  const baseSearchParams = {
    // Ignore legacy info-only filter so the shared toggle controls public-moorage visibility.
    ...rest,
    ...queryParams.originMaybe,
  };
  const defaultSort = resolveDefaultSortKey(config?.search?.sortConfig, baseSearchParams);
  const sortMaybe =
    !baseSearchParams[sortParamName] && defaultSort ? { [sortParamName]: defaultSort } : {};
  const searchParams = {
    ...baseSearchParams,
    ...sortMaybe,
    page: queryParams.page,
    perPage: RESULT_PAGE_SIZE,
    include: ['author', 'images'],
    'fields.listing': [
      'title',
      'geolocation',
      'price',
      'availabilityPlan',
      'publicData',
      'publicData.listingType',
      'publicData.transactionProcessAlias',
      'publicData.unitType',
      'publicData.pickupEnabled',
      'publicData.shippingEnabled',
    ],
    'fields.user': ['profile.displayName', 'profile.abbreviatedName'],
    'limit.images': 5,
    'fields.image': [
      'variants.scaled-small',
      'variants.scaled-medium',
      `variants.${variantPrefix}`,
      `variants.${variantPrefix}-2x`,
    ],
    ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
    ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
  };

  return searchParams;
};

const createCanonicalSearchParams = (search, config) => {
  const queryParams = extractQueryParams(search, config);
  return createSearchParams(config, queryParams);
};

const clearPendingMapHydrationTimeouts = (requestIdToKeep = null) => {
  pendingMapHydrationTimeouts.forEach((timeoutId, requestId) => {
    if (requestIdToKeep != null && requestId === requestIdToKeep) {
      return;
    }

    clearTimeout(timeoutId);
    pendingMapHydrationTimeouts.delete(requestId);
  });
};

const queryDefiningSearchParams = searchParams =>
  Object.entries(searchParams || {}).reduce((acc, [key, value]) => {
    const isRequestMetadataField = [
      'include',
      'fields.listing',
      'fields.user',
      'fields.image',
      'limit.images',
    ].includes(key);
    return isRequestMetadataField ? acc : { ...acc, [key]: value };
  }, {});

const createSearchRequestKey = searchParams => stringify(queryDefiningSearchParams(searchParams));

const publicMoorageSortPriority = listing =>
  isPublicMoorageListing(listing?.attributes?.publicData) ? 1 : 0;

const comparePublicMooragePriority = (a, b) =>
  publicMoorageSortPriority(a) - publicMoorageSortPriority(b);

const hasPublicMoorageListings = listings =>
  (listings || []).some(listing => isPublicMoorageListing(listing?.attributes?.publicData));

const compareLowestPrice = (a, b) => {
  const aPrice = a?.attributes?.price?.amount;
  const bPrice = b?.attributes?.price?.amount;
  const aHasPrice = Number.isFinite(aPrice);
  const bHasPrice = Number.isFinite(bPrice);

  if (aHasPrice !== bHasPrice) {
    return aHasPrice ? -1 : 1;
  }

  if (aHasPrice && bHasPrice && aPrice !== bPrice) {
    return aPrice - bPrice;
  }

  return 0;
};
// ================ Action types ================ //

export const SEARCH_LISTINGS_REQUEST = 'app/SearchPage/SEARCH_LISTINGS_REQUEST';
export const SEARCH_LISTINGS_SUCCESS = 'app/SearchPage/SEARCH_LISTINGS_SUCCESS';
export const SEARCH_LISTINGS_ERROR = 'app/SearchPage/SEARCH_LISTINGS_ERROR';

export const SEARCH_MAP_LISTINGS_REQUEST = 'app/SearchPage/SEARCH_MAP_LISTINGS_REQUEST';
export const SEARCH_MAP_LISTINGS_SUCCESS = 'app/SearchPage/SEARCH_MAP_LISTINGS_SUCCESS';
export const SEARCH_MAP_LISTINGS_ERROR = 'app/SearchPage/SEARCH_MAP_LISTINGS_ERROR';

export const SEARCH_MAP_SET_ACTIVE_LISTING = 'app/SearchPage/SEARCH_MAP_SET_ACTIVE_LISTING';

// ================ Reducer ================ //

const initialState = {
  pagination: null,
  searchParams: null,
  latestRequestId: null,
  latestRequestKey: null,
  searchInProgress: false,
  searchListingsError: null,
  currentPageResultIds: [],
};

export const resultIds = data => {
  const seenListingIds = new Set();

  return (data.data || []).reduce((ids, listing) => {
    const listingId = listing?.id;
    const listingUuid = listingId?.uuid;

    if (!listingUuid || seenListingIds.has(listingUuid)) {
      return ids;
    }

    seenListingIds.add(listingUuid);
    return [...ids, listingId];
  }, []);
};

const listingPageReducer = (state = initialState, action = {}) => {
  const { type, payload } = action;
  const isStaleResponse =
    payload?.requestId != null &&
    state.latestRequestId != null &&
    payload.requestId !== state.latestRequestId;

  switch (type) {
    case SEARCH_LISTINGS_REQUEST:
      return {
        ...state,
        searchParams: payload.searchParams,
        latestRequestId: payload.requestId ?? state.latestRequestId,
        latestRequestKey: payload.requestKey ?? state.latestRequestKey,
        searchInProgress: true,
        searchMapListingIds: [],
        searchListingsError: null,
      };
    case SEARCH_LISTINGS_SUCCESS:
      if (isStaleResponse) {
        return state;
      }
      return {
        ...state,
        currentPageResultIds: resultIds(payload.data),
        pagination: payload.data.meta,
        searchInProgress: false,
      };
    case SEARCH_LISTINGS_ERROR:
      if (isStaleResponse) {
        return state;
      }
      // eslint-disable-next-line no-console
      console.error(payload);
      return { ...state, searchInProgress: false, searchListingsError: payload };

    case SEARCH_MAP_SET_ACTIVE_LISTING:
      if (state.activeListingId?.uuid && state.activeListingId?.uuid === payload?.uuid) {
        return state;
      }
      return {
        ...state,
        activeListingId: payload,
      };
    default:
      return state;
  }
};

export default listingPageReducer;

// ================ Action creators ================ //

export const searchListingsRequest = (searchParams, requestId, requestKey) => ({
  type: SEARCH_LISTINGS_REQUEST,
  payload: { searchParams, requestId, requestKey },
});

export const searchListingsSuccess = (response, requestId, requestKey) => ({
  type: SEARCH_LISTINGS_SUCCESS,
  payload: { data: response.data, requestId, requestKey },
});

export const searchListingsError = (e, requestId, requestKey) => ({
  type: SEARCH_LISTINGS_ERROR,
  error: true,
  payload: { ...(e || {}), requestId, requestKey },
});

export const searchListings = (searchParams, config, options = {}) => (dispatch, getState, sdk) => {
  const currentSearchState = getState().SearchPage || {};
  const requestId = nextSearchRequestId();
  const requestKey = createSearchRequestKey(searchParams);
  const hasBounds = !!searchParams?.bounds;
  const isMapSearch = searchParams?.mapSearch === true;
  const force = options.force === true;

  if (
    !force &&
    currentSearchState.searchInProgress &&
    currentSearchState.latestRequestKey === requestKey &&
    inFlightSearchRequests.has(requestKey)
  ) {
    debugSearchLog('request:deduped-in-flight', {
      requestId,
      requestKey,
      hasBounds,
      isMapSearch,
    });
    return inFlightSearchRequests.get(requestKey);
  }

  if (
    !force &&
    !currentSearchState.searchInProgress &&
    currentSearchState.latestRequestKey === requestKey &&
    currentSearchState.pagination &&
    !currentSearchState.searchListingsError
  ) {
    debugSearchLog('request:deduped-loaded', {
      requestId,
      requestKey,
      hasBounds,
      isMapSearch,
    });
    return Promise.resolve();
  }

  clearPendingMapHydrationTimeouts();

  debugSearchLog('request:start', {
    requestId,
    requestKey,
    hasBounds,
    isMapSearch,
    searchParams,
  });
  dispatch(searchListingsRequest(searchParams, requestId, requestKey));

  // SearchPage can enforce listing query to only those listings with valid listingType
  // NOTE: this only works if you have set 'enum' type search schema to listing's public data fields
  //       - listingType
  //       Same setup could be expanded to 2 other extended data fields:
  //       - transactionProcessAlias
  //       - unitType
  //       ...and then turned enforceValidListingType config to true in configListing.js
  // Read More:
  // https://www.sharetribe.com/docs/how-to/manage-search-schemas-with-flex-cli/#adding-listing-search-schemas
  const searchValidListingTypes = listingTypes => {
    return config.listing.enforceValidListingType
      ? {
          pub_listingType: listingTypes.map(l => l.listingType),
          // pub_transactionProcessAlias: listingTypes.map(l => l.transactionType.alias),
          // pub_unitType: listingTypes.map(l => l.transactionType.unitType),
        }
      : {};
  };

  const omitInvalidCategoryParams = params => {
    const categoryConfig = config.search.defaultFilters?.find(f => f.schemaType === 'category');
    const categories = config.categoryConfiguration.categories;
    const { key: prefix, scope } = categoryConfig || {};
    const categoryParamPrefix = constructQueryParamName(prefix, scope);

    const validURLParamForCategoryData = (prefix, categories, level, params) => {
      const levelKey = `${categoryParamPrefix}${level}`;
      const levelValue = params?.[levelKey];
      const foundCategory = categories.find(cat => cat.id === levelValue);
      const subcategories = foundCategory?.subcategories || [];
      return foundCategory && subcategories.length > 0
        ? {
            [levelKey]: levelValue,
            ...validURLParamForCategoryData(prefix, subcategories, level + 1, params),
          }
        : foundCategory
        ? {
            [levelKey]: levelValue,
          }
        : {};
    };

    const categoryKeys = validURLParamForCategoryData(prefix, categories, 1, params);
    const nonCategoryKeys = Object.entries(params).reduce(
      (picked, [k, v]) =>
        k.startsWith(categoryParamPrefix)
          ? picked
          : {
              ...picked,
              [k]: v,
            },
      {}
    );

    return {
      ...nonCategoryKeys,
      ...categoryKeys,
    };
  };

  const priceSearchParams = priceParam => {
    const inSubunits = value => convertUnitToSubUnit(value, unitDivisor(config.currency));
    const values = priceParam ? priceParam.split(',') : [];
    return priceParam && values.length === 2
      ? {
          price: [inSubunits(values[0]), inSubunits(values[1]) + 1].join(','),
        }
      : {};
  };

  const datesSearchParams = datesParam => {
    const searchTZ = 'Etc/UTC';
    const datesFilter = config.search.defaultFilters.find(f => f.key === 'dates');
    const values = datesParam ? datesParam.split(',') : [];
    const hasValues = datesFilter && datesParam && values.length === 2;
    const { dateRangeMode, availability } = datesFilter || {};
    const isNightlyMode = dateRangeMode === 'night';
    const isEntireRangeAvailable = availability === 'time-full';

    // SearchPage need to use a single time zone but listings can have different time zones
    // We need to expand/prolong the time window (start & end) to cover other time zones too.
    //
    // NOTE: you might want to consider changing UI so that
    //   1) location is always asked first before date range
    //   2) use some 3rd party service to convert location to time zone (IANA tz name)
    //   3) Make exact dates filtering against that specific time zone
    //   This setup would be better for dates filter,
    //   but it enforces a UX where location is always asked first and therefore configurability
    const getProlongedStart = date => subtractTime(date, 14, 'hours', searchTZ);
    const getProlongedEnd = date => addTime(date, 12, 'hours', searchTZ);

    const startDate = hasValues ? parseDateFromISO8601(values[0], searchTZ) : null;
    const endRaw = hasValues ? parseDateFromISO8601(values[1], searchTZ) : null;
    const endDate =
      hasValues && isNightlyMode
        ? endRaw
        : hasValues
        ? getExclusiveEndDate(endRaw, searchTZ)
        : null;

    const today = getStartOf(new Date(), 'day', searchTZ);
    const possibleStartDate = subtractTime(today, 14, 'hours', searchTZ);
    const hasValidDates =
      hasValues &&
      startDate.getTime() >= possibleStartDate.getTime() &&
      startDate.getTime() <= endDate.getTime();

    const dayCount = isEntireRangeAvailable ? daysBetween(startDate, endDate) : 1;
    const day = 1440;
    const hour = 60;
    // When entire range is required to be available, we count minutes of included date range,
    // but there's a need to subtract one hour due to possibility of daylight saving time.
    // If partial range is needed, then we just make sure that the shortest time unit supported
    // is available within the range.
    // You might want to customize this to match with your time units (e.g. day: 1440 - 60)
    const minDuration = isEntireRangeAvailable ? dayCount * day - hour : hour;
    return hasValidDates
      ? {
          start: getProlongedStart(startDate),
          end: getProlongedEnd(endDate),
          // Availability can be time-full or time-partial.
          // However, due to prolonged time window, we need to use time-partial.
          availability: 'time-partial',
          // minDuration uses minutes
          minDuration,
        }
      : {};
  };

  const stockFilters = datesMaybe => {
    const hasDatesFilterInUse = Object.keys(datesMaybe).length > 0;

    // If dates filter is not in use,
    //   1) Add minStock filter with default value (1)
    //   2) Add relaxed stockMode: "match-undefined"
    // The latter is used to filter out all the listings that explicitly are out of stock,
    // but keeps bookable and inquiry listings.
    return hasDatesFilterInUse
      ? {}
      : {
          minStock: 1,
          stockMode: 'match-undefined',
        };
  };

  const {
    perPage,
    price,
    dates,
    sort,
    mapSearch,
    publicMoorages,
    [ACCESS_FILTER_QUERY_PARAM]: access,
    ...restOfParams
  } = searchParams;
  const priceMaybe = priceSearchParams(price);
  const datesMaybe = datesSearchParams(dates);
  const stockMaybe = stockFilters(datesMaybe);
  const accessFilter = normalizeAccessFilter(access || (publicMoorages ? ACCESS_FILTER_ALL : null));
  const shouldFilterAccess = accessFilter !== ACCESS_FILTER_ALL;
  // const seatsMaybe = seatsSearchParams(seats, datesMaybe);
  const sortMaybe = !sort || sort === config.search.sortConfig.relevanceKey
    ? {}
    : {
        sort,
      };
  const params = {
    // The rest of the params except invalid nested category-related params
    // Note: invalid independent search params are still passed through
    ...omitInvalidCategoryParams(restOfParams),
    ...priceMaybe,
    ...datesMaybe,
    ...stockMaybe,
    // ...seatsMaybe,
    ...sortMaybe,
    ...searchValidListingTypes(config.listing.listingTypes),
    perPage,
  };
  const sortParamName = config?.search?.sortConfig?.queryParamName || 'sort';
  const activeSort = sort || resolveDefaultSortKey(config?.search?.sortConfig, searchParams);
  const isLowestPriceSort = activeSort === '-price' && sortParamName === 'sort';
  let didApplyPartialResponse = false;
  let didFetchAllPagesForClientSort = false;

  const createFilteredResponse = (response, options = {}) => {
    const {
      clientSidePagination = false,
      partialPublicMoorageResults = false,
      unpaginatedMapResults = false,
    } = options;
    const isUnpaginatedResponse =
      partialPublicMoorageResults ||
      unpaginatedMapResults ||
      response.data.meta?.paginationUnsupported;
    const filteredData =
      accessFilter === ACCESS_FILTER_PUBLIC
        ? response.data.data.filter(listing =>
            isPublicMoorageListing(listing?.attributes?.publicData)
          )
        : accessFilter === ACCESS_FILTER_PRIVATE
        ? response.data.data.filter(
            listing => !isPublicMoorageListing(listing?.attributes?.publicData)
          )
        : response.data.data;
    const sortedData = [...filteredData].sort((a, b) => {
      const publicMooragePriority = comparePublicMooragePriority(a, b);
      if (publicMooragePriority !== 0) {
        return publicMooragePriority;
      }

      if (isLowestPriceSort) {
        return compareLowestPrice(a, b);
      }

      // Fallback: keep original order within the same listing type group.
      return 0;
    });

    const pageFromResponse = Number(response.data.meta?.page || params.page || 1);
    const perPageFromResponse = Number(response.data.meta?.perPage || RESULT_PAGE_SIZE);
    const shouldClientPaginate =
      (shouldFilterAccess || clientSidePagination) && !isUnpaginatedResponse;
    const paginatedData = shouldClientPaginate
      ? sortedData.slice(
          Math.max(pageFromResponse - 1, 0) * perPageFromResponse,
          Math.max(pageFromResponse, 0) * perPageFromResponse
        )
      : sortedData;
    const totalPages =
      sortedData.length > 0 ? Math.ceil(sortedData.length / perPageFromResponse) : 0;
    const filteredMeta = isUnpaginatedResponse
      ? {
          ...response.data.meta,
          page: pageFromResponse,
          perPage: perPageFromResponse,
          totalItems: paginatedData.length,
          totalPages: 1,
          paginationUnsupported: true,
        }
      : {
          ...response.data.meta,
          page: pageFromResponse,
          totalItems: sortedData.length,
          totalPages,
        };

    return {
      ...response,
      data: {
        ...response.data,
        meta: filteredMeta,
        data: paginatedData,
      },
    };
  };

  const applySearchResponse = (response, options = {}) => {
    const {
      clientSidePagination = false,
      partialPublicMoorageResults = false,
      unpaginatedMapResults = false,
      logEvent = 'request:applied-success',
    } = options;
    const latestRequestId = getState().SearchPage?.latestRequestId;
    if (latestRequestId !== requestId) {
      debugSearchLog('request:ignored-stale-success', {
        requestId,
        latestRequestId,
        requestKey,
        hasBounds,
        isMapSearch,
      });
      return response;
    }

    const filteredResponse = createFilteredResponse(response, {
      clientSidePagination,
      partialPublicMoorageResults,
      unpaginatedMapResults,
    });
    const listingFields = config?.listing?.listingFields;
    const sanitizeConfig = {
      listingFields,
    };
    dispatch(addMarketplaceEntities(filteredResponse, sanitizeConfig));
    dispatch(searchListingsSuccess(filteredResponse, requestId, requestKey));
    debugSearchLog(logEvent, {
      requestId,
      requestKey,
      hasBounds,
      isMapSearch,
      partialPublicMoorageResults,
      unpaginatedMapResults,
      results: filteredResponse.data.data.length,
    });
    return response;
  };

  const fetchAllSearchPages = response => {
    const meta = response.data.meta || {};
    const totalPages = Number(meta.totalPages);
    const currentPage = Number(meta.page || params.page || 1);

    if (
      meta.paginationUnsupported ||
      !Number.isInteger(totalPages) ||
      totalPages <= 1
    ) {
      return Promise.resolve(response);
    }

    const pageRequests = Array.from({ length: totalPages }, (_, index) => index + 1)
      .filter(pageNumber => pageNumber !== currentPage)
      .map(pageNumber => sdk.listings.query({ ...params, page: pageNumber }));

    return Promise.all(pageRequests).then(pageResponses => {
      const orderedResponses = [response, ...pageResponses].sort((a, b) => {
        const aPage = Number(a.data.meta?.page || 1);
        const bPage = Number(b.data.meta?.page || 1);
        return aPage - bPage;
      });

      return {
        ...response,
        data: {
          ...response.data,
          data: orderedResponses.flatMap(pageResponse => pageResponse.data.data || []),
          included: orderedResponses.flatMap(pageResponse => pageResponse.data.included || []),
        },
      };
    });
  };

  const requestPromise = sdk.listings
    .query(params)
    .then(response => {
      const latestRequestId = getState().SearchPage?.latestRequestId;
      if (latestRequestId !== requestId) {
        debugSearchLog('request:ignored-stale-success', {
          requestId,
          latestRequestId,
          requestKey,
          hasBounds,
          isMapSearch,
        });
        return response;
      }

      const meta = response.data.meta || {};
      const totalPages = Number(meta.totalPages);
      const shouldHydrateMapPages =
        isMapSearch &&
        !meta.paginationUnsupported &&
        Number.isInteger(totalPages) &&
        totalPages > 1;
      const shouldHydrateClientSortedPages =
        !isMapSearch &&
        accessFilter === ACCESS_FILTER_ALL &&
        !meta.paginationUnsupported &&
        Number.isInteger(totalPages) &&
        totalPages > 1;

      if (!shouldHydrateMapPages) {
        if (shouldFilterAccess) {
          return fetchAllSearchPages(response);
        }
        if (shouldHydrateClientSortedPages) {
          didFetchAllPagesForClientSort = true;
          return fetchAllSearchPages(response);
        }
        return response;
      }

      if (accessFilter === ACCESS_FILTER_ALL && hasPublicMoorageListings(response.data.data)) {
        didFetchAllPagesForClientSort = true;
        return fetchAllSearchPages(response);
      }

      didApplyPartialResponse = true;
      applySearchResponse(response, {
        partialPublicMoorageResults: shouldFilterAccess,
        unpaginatedMapResults: true,
        logEvent: 'request:applied-partial-success',
      });

      const hydrationDelayMs = shouldFilterAccess
        ? MAP_SEARCH_PUBLIC_MOORAGE_HYDRATE_DELAY_MS
        : 0;
      const timeoutId = setTimeout(() => {
        pendingMapHydrationTimeouts.delete(requestId);

        const latestHydrationRequestId = getState().SearchPage?.latestRequestId;
        if (latestHydrationRequestId !== requestId) {
          debugSearchLog('request:skipped-hydration-stale', {
            requestId,
            latestRequestId: latestHydrationRequestId,
            requestKey,
            hasBounds,
            isMapSearch,
          });
          return;
        }

        fetchAllSearchPages(response)
          .then(fullResponse =>
            applySearchResponse(fullResponse, {
              partialPublicMoorageResults: false,
              unpaginatedMapResults: isMapSearch,
              logEvent: 'request:applied-hydrated-success',
            })
          )
          .catch(error => {
            const hydrationError = storableError(error);
            debugSearchLog('request:hydration-error', {
              requestId,
              requestKey,
              hasBounds,
              isMapSearch,
              error: hydrationError?.message,
            });
          });
      }, hydrationDelayMs);

      pendingMapHydrationTimeouts.set(requestId, timeoutId);
      return response;
    })
    .then(response => {
      if (didApplyPartialResponse) {
        return response;
      }

      return applySearchResponse(response, {
        clientSidePagination: didFetchAllPagesForClientSort,
        unpaginatedMapResults: isMapSearch,
      });
    })
    .catch(e => {
      const error = storableError(e);
      const latestRequestId = getState().SearchPage?.latestRequestId;
      if (latestRequestId !== requestId) {
        debugSearchLog('request:ignored-stale-error', {
          requestId,
          latestRequestId,
          requestKey,
          hasBounds,
          isMapSearch,
          error: error?.message,
        });
        return;
      }

      dispatch(searchListingsError(error, requestId, requestKey));
      debugSearchLog('request:applied-error', {
        requestId,
        requestKey,
        hasBounds,
        isMapSearch,
        error: error?.message,
      });
      if (!(isErrorUserPendingApproval(error) || isForbiddenError(error))) {
        throw e;
      }
    })
    .finally(() => {
      if (inFlightSearchRequests.get(requestKey) === requestPromise) {
        inFlightSearchRequests.delete(requestKey);
      }
    });

  inFlightSearchRequests.set(requestKey, requestPromise);
  return requestPromise;
};

export const setActiveListing = listingId => ({
  type: SEARCH_MAP_SET_ACTIVE_LISTING,
  payload: listingId,
});

export const fetchListings = params => async (dispatch, getState, sdk) => {
  const searchParams = createCanonicalSearchParams(params.search, params.config);

  return dispatch(searchListings(searchParams, params.config, { force: params.force === true }));
};

export const loadData = (params, search, config) => {
  const searchParams = createCanonicalSearchParams(search, config);

  return searchListings(searchParams, config);
};
